import { html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { ExtBaseElement } from '../../core/ExtBaseElement';
import './ext-toast.css';

export type ExtToastKind = 'success' | 'danger' | 'warning' | 'info';

/**
 * <ext-toast>
 *
 * Representa UN toast individual. El consumidor tipico usa la API imperativa
 * window.ExtUI.toast.show({...}), no instancia el elemento a mano. Pero es
 * usable directamente para casos declarativos (ej. toast fijo en un form).
 *
 * Props:
 *  - kind:     success | danger | warning | info (default info)
 *  - message:  string
 *  - duration: ms antes de auto-dismiss (default 3000; 0 = sin auto-dismiss)
 *
 * Evento: ext:dismiss cuando se cierra (manual o auto).
 */
@customElement('ext-toast')
export class ExtToast extends ExtBaseElement {
  @property({ type: String, reflect: true }) kind: ExtToastKind = 'info';
  @property({ type: String }) message = '';
  @property({ type: Number }) duration = 3000;

  private _timerId: number | null = null;

  connectedCallback(): void {
    super.connectedCallback();
    this._scheduleAutoDismiss();
  }

  disconnectedCallback(): void {
    super.disconnectedCallback();
    this._clearTimer();
  }

  private _scheduleAutoDismiss(): void {
    this._clearTimer();
    if (this.duration > 0) {
      this._timerId = window.setTimeout(() => {
        this._dismiss();
      }, this.duration);
    }
  }

  private _clearTimer(): void {
    if (this._timerId !== null) {
      window.clearTimeout(this._timerId);
      this._timerId = null;
    }
  }

  private _dismiss = (): void => {
    this._clearTimer();
    this._emit('dismiss', { id: this.id });
  };

  render() {
    return html`
      <div class="ext-toast__root" role="status" aria-live="polite">
        <span class="ext-toast__message">${this.message}</span>
        <button
          type="button"
          class="ext-toast__close"
          aria-label="Cerrar"
          @click="${this._dismiss}"
        >
          &times;
        </button>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'ext-toast': ExtToast;
  }
}
